import React, { memo, useCallback, useMemo, useState } from 'react'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import type { LoadableComponent } from '@loadable/component'
import classNames from 'classnames'
import type { IRegion } from '@/types'
import { useIsomorphicLayoutEffect } from '@/utils/hooks'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import type { EUserRegionSelectVariation, IUserRegionSelectProps, TUserRegionSelectResponsiveConfig } from './types'
import type { IUserRegionSelectConfirmProps } from './UserRegionSelectConfirm'
import { UserRegionSelectConfirmDesktop, UserRegionSelectConfirmMobile } from './UserRegionSelectConfirm'
import type { IUserRegionSelectPickerProps } from './UserRegionSelectPicker'
import { UserRegionSelectPickerDesktop, UserRegionSelectPickerMobile } from './UserRegionSelectPicker'
import type { IUserRegionSelectSelectorProps } from './UserRegionSelectSelector'
import { UserRegionSelectSelectorDesktop, UserRegionSelectSelectorMobile } from './UserRegionSelectSelector'
import './styles.scss'

const UserRegionSelect: React.FC<IUserRegionSelectProps> = props => {
  const {
    className,
    label,
    picker: pickerConfig,
    confirm: confirmConfig,
    selector: selectorConfig,
    onClickPicker,
    showSelector,
    genitiveCase,
  } = props

  const { isRegionConfirmed, setUserRegion, userRegion } = useUserRegion()

  const { isDesktop, isTablet, isMobile } = useDeviceType()

  const selectRegion = useCallback(
    (newRegion: IRegion) => {
      setUserRegion(newRegion)
    },
    [setUserRegion],
  )

  const [isConfirmVisible, setIsConfirmVisible] = useState(false)
  const openConfirm = () => setIsConfirmVisible(true)
  const closeConfirm = () => setIsConfirmVisible(false)

  const [isSelectorShown, setIsSelectorShown] = useState(false)
  const openSelector = () => setIsSelectorShown(true)
  const closeSelector = () => setIsSelectorShown(false)

  const pickerProps: IUserRegionSelectPickerProps = useMemo(
    () => ({
      region: userRegion,
      label,
      onClick: () => openSelector(),
      showSelector,
      genitiveCase,
    }),
    [userRegion, label, showSelector, genitiveCase],
  )

  const confirmProps: Maybe<IUserRegionSelectConfirmProps> = useMemo(() => {
    if (!userRegion) return null

    return {
      region: userRegion,
      isShown: isConfirmVisible,
      onAccept: () => {
        setUserRegion(userRegion)
        closeConfirm()
      },
      onDecline: () => openSelector(),
    } as IUserRegionSelectConfirmProps
  }, [setUserRegion, userRegion, isConfirmVisible])

  const selectorProps: IUserRegionSelectSelectorProps = useMemo(
    () => ({
      value: userRegion,
      isShown: isSelectorShown,
      onClose: () => closeSelector(),
      onInput: newRegion => {
        selectRegion(newRegion)
        closeConfirm()
        closeSelector()
      },
    }),
    [userRegion, isSelectorShown, selectRegion],
  )

  useIsomorphicLayoutEffect(() => {
    if (userRegion.code && !isRegionConfirmed) {
      openConfirm()
    }
  }, [userRegion.code, isRegionConfirmed])

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  function getSuitableComponent<T extends React.FC<any> | LoadableComponent<any>>(
    variations: Record<EUserRegionSelectVariation, T>,
    config: TUserRegionSelectResponsiveConfig,
  ): Maybe<T> {
    if (!config) return null

    if (isDesktop && config.desktop) return variations[config.desktop]
    if (isTablet && config.tablet) return variations[config.tablet]
    if (isMobile && config.mobile) return variations[config.mobile]

    return null
  }

  const Picker = getSuitableComponent({ desktop: UserRegionSelectPickerDesktop, mobile: UserRegionSelectPickerMobile }, pickerConfig)

  const Confirm =
    confirmProps && !isRegionConfirmed
      ? getSuitableComponent({ desktop: UserRegionSelectConfirmDesktop, mobile: UserRegionSelectConfirmMobile }, confirmConfig)
      : null

  const Selector =
    Picker || Confirm
      ? getSuitableComponent({ desktop: UserRegionSelectSelectorDesktop, mobile: UserRegionSelectSelectorMobile }, selectorConfig)
      : null

  const isVisible = !!(Picker || Confirm || Selector)

  const onClickRegionPicker = () => {
    onClickPicker?.()
    pickerProps.onClick()
  }

  return isVisible ? (
    <div className={classNames('userRegionSelect', className)}>
      {Picker && <Picker {...pickerProps} onClick={onClickRegionPicker} />}
      {Confirm && confirmProps && <Confirm {...confirmProps} />}
      {Selector && <Selector {...selectorProps} />}
    </div>
  ) : null
}

export default memo(UserRegionSelect)
